import { LoaderCircle, Pencil, Save, Trash2, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import type { UpdateHistoryEntryInput } from '@calorie-tracker/api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

import { fieldClassName } from './constants';
import {
  createEditableMacroState,
  formatMealTime,
  formatMealTypeLabel,
  formatNumber,
  formatRemainingLabel,
  formatSourceLabel,
  getErrorMessage,
  getTodayStatusMeta,
} from './helpers';
import type { AppEntrySummary, AppTodaySummary } from './types';

interface TodayEntryEditorState {
  calories: string;
  carbsGrams: string;
  fatGrams: string;
  inputQuantityAmount: string;
  inputQuantityUnit: string;
  normalizedName: string;
  proteinGrams: string;
}

function MacroLine({
  label,
  remaining,
  total,
  target,
}: {
  label: string;
  remaining: number;
  total: number;
  target: number | undefined;
}) {
  const progress = target && target > 0 ? Math.min(100, Math.max(0, (total / target) * 100)) : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {formatNumber(total)}g
          {target ? (
            <span className="text-muted-foreground"> / {formatNumber(target)}g</span>
          ) : null}
        </span>
      </div>
      <div className="h-px bg-border">
        <div className="h-px bg-foreground" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs text-muted-foreground">
        {target ? formatRemainingLabel(remaining, 'g') : 'No target'}
      </p>
    </div>
  );
}

export function TodayScreen({
  onNavigateToLog,
  summary,
  summaryError,
}: {
  onNavigateToLog: () => void;
  summary: AppTodaySummary | undefined;
  summaryError: string | undefined;
}) {
  const utils = trpc.useUtils();
  const updateEntry = trpc.history.updateEntry.useMutation();
  const deleteEntry = trpc.history.deleteEntry.useMutation();
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<TodayEntryEditorState | null>(null);
  const [todayActionError, setTodayActionError] = useState<string | null>(null);
  const totals = summary?.totals ?? {
    calories: 0,
    proteinGrams: 0,
    carbsGrams: 0,
    fatGrams: 0,
  };
  const goal = summary?.goal ?? null;
  const remaining = summary?.remaining ?? {
    calories: 0,
    proteinGrams: 0,
    carbsGrams: 0,
    fatGrams: 0,
  };
  const mealSummaries = summary?.meals ?? [];
  const statusMeta = getTodayStatusMeta(summary?.status ?? 'no-goal');
  const calorieDelta =
    goal === null
      ? 'Daily goals will show up here once one is saved.'
      : remaining.calories >= 0
        ? `${formatNumber(remaining.calories)} calories remaining`
        : `${formatNumber(Math.abs(remaining.calories))} calories over`;
  const isMutating = updateEntry.isPending || deleteEntry.isPending;

  function startEditingEntry(entry: AppEntrySummary) {
    setEditingEntryId(entry.id);
    setEditorState(createEditableMacroState(entry));
    setTodayActionError(null);
  }

  function updateEditorField<Key extends keyof TodayEntryEditorState>(
    key: Key,
    value: TodayEntryEditorState[Key],
  ) {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        [key]: value,
      };
    });
  }

  async function invalidateToday(date: string) {
    await utils.today.summary.invalidate({ date });
  }

  async function submitEntryEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!editingEntryId || !editorState) {
      return;
    }

    const amount = Number(editorState.inputQuantityAmount);
    const calories = Number(editorState.calories);
    const proteinGrams = Number(editorState.proteinGrams);
    const carbsGrams = Number(editorState.carbsGrams);
    const fatGrams = Number(editorState.fatGrams);

    if (
      !Number.isFinite(amount) ||
      !Number.isFinite(calories) ||
      !Number.isFinite(proteinGrams) ||
      !Number.isFinite(carbsGrams) ||
      !Number.isFinite(fatGrams)
    ) {
      setTodayActionError('Enter valid numbers before saving changes.');
      return;
    }

    setTodayActionError(null);

    const input: UpdateHistoryEntryInput = {
      entryId: editingEntryId,
      normalizedName: editorState.normalizedName,
      inputQuantity: {
        amount,
        unit: editorState.inputQuantityUnit,
      },
      macros: {
        calories,
        proteinGrams,
        carbsGrams,
        fatGrams,
      },
    };

    try {
      const result = await updateEntry.mutateAsync(input);
      await invalidateToday(result.date);
      setEditingEntryId(null);
      setEditorState(null);
    } catch (error) {
      setTodayActionError(getErrorMessage(error));
    }
  }

  async function removeEntry(entryId: string) {
    if (!window.confirm('Delete this saved entry?')) {
      return;
    }

    setTodayActionError(null);

    try {
      const result = await deleteEntry.mutateAsync({ entryId });
      await invalidateToday(result.date);
      setEditingEntryId((currentId) => (currentId === entryId ? null : currentId));
      setEditorState((currentState) => (editingEntryId === entryId ? null : currentState));
    } catch (error) {
      setTodayActionError(getErrorMessage(error));
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Today</p>
            <div className="flex items-end gap-2">
              <h2 className="text-5xl font-semibold leading-none">
                {formatNumber(totals.calories)}
              </h2>
              <p className="pb-1 text-sm text-muted-foreground">
                {goal ? `/ ${formatNumber(goal.calories)} cal` : 'calories logged'}
              </p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{calorieDelta}</p>
          </div>
          <Button className="shrink-0" onClick={onNavigateToLog} size="sm">
            Log
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 border-y border-border py-3">
          <div>
            <p className="text-sm font-medium">Status</p>
            <p className="mt-1 text-xs text-muted-foreground">{statusMeta.description}</p>
          </div>
          <span className={`rounded px-2 py-1 text-xs font-medium ${statusMeta.badgeClassName}`}>
            {statusMeta.label}
          </span>
        </div>

        <div className="space-y-4">
          <MacroLine
            label="Protein"
            remaining={remaining.proteinGrams}
            target={goal?.proteinGrams}
            total={totals.proteinGrams}
          />
          <MacroLine
            label="Carbs"
            remaining={remaining.carbsGrams}
            target={goal?.carbsGrams}
            total={totals.carbsGrams}
          />
          <MacroLine
            label="Fat"
            remaining={remaining.fatGrams}
            target={goal?.fatGrams}
            total={totals.fatGrams}
          />
        </div>

        {summary?.reviewCount ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {summary.reviewCount} entr{summary.reviewCount === 1 ? 'y needs' : 'ies need'} review
            before you should trust the estimate.
          </p>
        ) : null}

        {summaryError ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {summaryError}
          </p>
        ) : null}

        {todayActionError ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {todayActionError}
          </p>
        ) : null}
      </Card>

      <Card className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Meals</p>
            <h3 className="mt-1 text-xl font-semibold">Timeline</h3>
          </div>
          <p className="text-sm text-muted-foreground">{summary?.date ?? 'No logs yet'}</p>
        </div>

        {mealSummaries.length === 0 ? (
          <div className="border border-dashed border-border px-3 py-5 text-sm leading-6 text-muted-foreground">
            Nothing logged yet. Use Log to add a meal, snack, or manual entry.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {mealSummaries.map(({ meal, totals: mealTotals, entries }) => (
              <section className="space-y-3 py-4 first:pt-0 last:pb-0" key={meal.id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{formatMealTypeLabel(meal.mealType)}</p>
                    <h4 className="mt-1 text-xs text-muted-foreground">
                      {formatMealTime(meal.consumedAt)}
                    </h4>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{formatNumber(mealTotals.calories)} cal</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatNumber(mealTotals.proteinGrams)}p /{' '}
                      {formatNumber(mealTotals.carbsGrams)}c / {formatNumber(mealTotals.fatGrams)}f
                    </p>
                  </div>
                </div>

                <div className="space-y-1">
                  {entries.map((entry) => {
                    const isEditing = entry.id === editingEntryId;

                    return (
                      <div
                        className="space-y-3 border-t border-border/70 py-3 first:border-t-0"
                        key={entry.id}
                      >
                        {isEditing && editorState ? (
                          <form className="space-y-3" onSubmit={submitEntryEdit}>
                            <label className="grid gap-2 text-sm">
                              <span className="font-medium">Food</span>
                              <input
                                className={fieldClassName}
                                onChange={(event) =>
                                  updateEditorField('normalizedName', event.target.value)
                                }
                                required
                                type="text"
                                value={editorState.normalizedName}
                              />
                            </label>

                            <div className="grid grid-cols-2 gap-3">
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Amount</span>
                                <input
                                  className={fieldClassName}
                                  min="0.1"
                                  onChange={(event) =>
                                    updateEditorField('inputQuantityAmount', event.target.value)
                                  }
                                  required
                                  step="0.1"
                                  type="number"
                                  value={editorState.inputQuantityAmount}
                                />
                              </label>
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Unit</span>
                                <input
                                  className={fieldClassName}
                                  onChange={(event) =>
                                    updateEditorField('inputQuantityUnit', event.target.value)
                                  }
                                  required
                                  type="text"
                                  value={editorState.inputQuantityUnit}
                                />
                              </label>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Calories</span>
                                <input
                                  className={fieldClassName}
                                  min="0"
                                  onChange={(event) =>
                                    updateEditorField('calories', event.target.value)
                                  }
                                  required
                                  step="1"
                                  type="number"
                                  value={editorState.calories}
                                />
                              </label>
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Protein (g)</span>
                                <input
                                  className={fieldClassName}
                                  min="0"
                                  onChange={(event) =>
                                    updateEditorField('proteinGrams', event.target.value)
                                  }
                                  required
                                  step="0.1"
                                  type="number"
                                  value={editorState.proteinGrams}
                                />
                              </label>
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Carbs (g)</span>
                                <input
                                  className={fieldClassName}
                                  min="0"
                                  onChange={(event) =>
                                    updateEditorField('carbsGrams', event.target.value)
                                  }
                                  required
                                  step="0.1"
                                  type="number"
                                  value={editorState.carbsGrams}
                                />
                              </label>
                              <label className="grid gap-2 text-sm">
                                <span className="font-medium">Fat (g)</span>
                                <input
                                  className={fieldClassName}
                                  min="0"
                                  onChange={(event) =>
                                    updateEditorField('fatGrams', event.target.value)
                                  }
                                  required
                                  step="0.1"
                                  type="number"
                                  value={editorState.fatGrams}
                                />
                              </label>
                            </div>

                            <div className="flex gap-2">
                              <Button disabled={isMutating} size="sm" type="submit">
                                {updateEntry.isPending ? (
                                  <>
                                    <LoaderCircle className="h-4 w-4 animate-spin" />
                                    Saving
                                  </>
                                ) : (
                                  <>
                                    <Save className="h-4 w-4" />
                                    Save
                                  </>
                                )}
                              </Button>
                              <Button
                                disabled={isMutating}
                                onClick={() => {
                                  setEditingEntryId(null);
                                  setEditorState(null);
                                  setTodayActionError(null);
                                }}
                                size="sm"
                                type="button"
                                variant="outline"
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">
                                {entry.normalizedName}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {formatNumber(entry.inputQuantity.amount)}{' '}
                                {entry.inputQuantity.unit} • {formatSourceLabel(entry.source)}
                              </p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-sm font-semibold">
                                {formatNumber(entry.macros.calories)} cal
                              </p>
                              <p
                                className={`mt-1 text-xs ${
                                  entry.requiresReview ? 'text-amber-700' : 'text-muted-foreground'
                                }`}
                              >
                                {entry.requiresReview ? 'Needs review' : 'Ready'}
                              </p>
                            </div>
                          </div>
                        )}

                        {!isEditing ? (
                          <div className="flex gap-2">
                            <Button
                              disabled={isMutating}
                              onClick={() => startEditingEntry(entry)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              className="text-red-700 hover:text-red-700"
                              disabled={isMutating}
                              onClick={() => void removeEntry(entry.id)}
                              size="sm"
                              type="button"
                              variant="outline"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
